import { useEffect, useState, useCallback } from 'react';
import { useLocationData } from '../../../hooks/useLocationData';
import { CascadingLocationSelectorProps, LocationSelectorState } from '../types';
import { Country, State, City } from '../../../services/locationAPI';

export const useCascadingLocationSelector = (props: CascadingLocationSelectorProps) => {
  const {
    selectedCountry = '',
    selectedState = '',
    // selectedCity = '', // unused - commented out
    onCountryChange,
    onStateChange,
    onCityChange,
    // required = { // unused - commented out
    //   country: true,
    //   state: true,
    //   city: true
    // }
  } = props;

  const {
    countries,
    states,
    cities,
    loading,
    error,
    loadStates,
    loadCities,
    clearStates,
    clearCities
  } = useLocationData();

  // Internal state
  const [state, setState] = useState<LocationSelectorState>({
    selectedCountryISO: '',
    selectedStateISO: '',
    isOpen: {
      country: false,
      state: false,
      city: false
    },
    searchTerms: {
      country: '',
      state: '',
      city: ''
    }
  });

  // Initialize country ISO based on selected country name
  useEffect(() => {
    if (selectedCountry && countries.length > 0) {
      const country = countries.find(c => c.name === selectedCountry);
      if (country && country.iso2 !== state.selectedCountryISO) {
        setState(prev => ({ ...prev, selectedCountryISO: country.iso2 }));
        loadStates(country.iso2);
      }
    }
  }, [selectedCountry, countries, loadStates, state.selectedCountryISO]);

  // Initialize state ISO based on selected state name
  useEffect(() => {
    if (selectedState && states.length > 0) {
      const stateData = states.find(s => s.name === selectedState);
      if (stateData && stateData.iso2 !== state.selectedStateISO) {
        setState(prev => ({ ...prev, selectedStateISO: stateData.iso2 }));
        loadCities(state.selectedCountryISO, stateData.iso2);
      }
    }
  }, [selectedState, states, loadCities, state.selectedCountryISO, state.selectedStateISO]);

  // Handle country selection
  const handleCountrySelect = useCallback((country: Country) => {
    setState(prev => ({
      ...prev,
      selectedCountryISO: country.iso2,
      selectedStateISO: '',
      isOpen: { ...prev.isOpen, country: false },
      searchTerms: { ...prev.searchTerms, country: '' }
    }));

    clearStates();
    clearCities();
    
    onCountryChange(country.name, country);
    onStateChange('');
    onCityChange('');

    if (country.iso2) {
      loadStates(country.iso2);
    }
  }, [onCountryChange, onStateChange, onCityChange, clearStates, clearCities, loadStates]);

  // Handle state selection
  const handleStateSelect = useCallback((stateData: State) => {
    setState(prev => ({
      ...prev,
      selectedStateISO: stateData.iso2,
      isOpen: { ...prev.isOpen, state: false },
      searchTerms: { ...prev.searchTerms, state: '' }
    }));

    clearCities();
    
    onStateChange(stateData.name, stateData);
    onCityChange('');

    if (state.selectedCountryISO && stateData.iso2) {
      loadCities(state.selectedCountryISO, stateData.iso2);
    }
  }, [onStateChange, onCityChange, clearCities, loadCities, state.selectedCountryISO]);

  // Handle city selection
  const handleCitySelect = useCallback((city: City) => {
    setState(prev => ({
      ...prev,
      isOpen: { ...prev.isOpen, city: false },
      searchTerms: { ...prev.searchTerms, city: '' }
    }));

    onCityChange(city.name, city);
  }, [onCityChange]);

  // Toggle dropdown open state
  const toggleDropdown = useCallback((type: 'country' | 'state' | 'city') => {
    setState(prev => ({
      ...prev,
      isOpen: {
        ...prev.isOpen,
        [type]: !prev.isOpen[type]
      }
    }));
  }, []);

  // Update search term
  const updateSearchTerm = useCallback((type: 'country' | 'state' | 'city', term: string) => {
    setState(prev => ({
      ...prev,
      searchTerms: {
        ...prev.searchTerms,
        [type]: term
      }
    }));
  }, []);

  // Close all dropdowns
  const closeAllDropdowns = useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: {
        country: false,
        state: false,
        city: false
      },
      searchTerms: {
        country: '',
        state: '',
        city: ''
      }
    }));
  }, []);

  return {
    // Data
    countries,
    states,
    cities,
    loading,
    error,
    
    // State
    ...state,
    
    // Actions
    handleCountrySelect,
    handleStateSelect,
    handleCitySelect,
    toggleDropdown,
    updateSearchTerm,
    closeAllDropdowns
  };
};
